/**
 * GolfGameModeSelector Component
 *
 * Mode selection screen for the Golf card game offering:
 * - Single-player with Easy/Medium/Hard difficulty
 * - Local multiplayer with player count (2-4) and custom names
 * - Online multiplayer with Create Room / Join Room
 */

import React, { useState } from 'react';
import { GolfGameSettings, GolfDifficulty } from '../types';
import './GolfGameModeSelector.css';

export interface GolfGameModeSelectorProps {
  onStartGame: (settings: GolfGameSettings) => void;
  onJoinPrivateGame: (roomCode: string) => Promise<void>;
  guestGamesRemaining?: number;
  isAuthenticated?: boolean;
}

type SelectionStep = 'mode' | 'difficulty' | 'local-setup' | 'online-setup';

const GolfGameModeSelector: React.FC<GolfGameModeSelectorProps> = ({
  onStartGame,
  onJoinPrivateGame,
  guestGamesRemaining,
  isAuthenticated = false,
}) => {
  const [step, setStep] = useState<SelectionStep>('mode');
  const [playerCount, setPlayerCount] = useState(2);
  const [playerNames, setPlayerNames] = useState<string[]>(['Player 1', 'Player 2', 'Player 3', 'Player 4']);
  const [roomCode, setRoomCode] = useState('');
  const [roomCodeError, setRoomCodeError] = useState('');
  const [isJoining, setIsJoining] = useState(false);

  const handleBack = () => {
    setStep('mode');
    setRoomCodeError('');
  };

  const handleModeSelect = (mode: 'singleplayer' | 'local-multiplayer' | 'multiplayer-realtime') => {
    if (mode === 'singleplayer') {
      setStep('difficulty');
    } else if (mode === 'local-multiplayer') {
      setStep('local-setup');
    } else {
      setStep('online-setup');
    }
  };

  const handleDifficultySelect = (difficulty: GolfDifficulty) => {
    onStartGame({
      mode: 'singleplayer',
      aiDifficulty: difficulty,
      playerCount: 2,
      playerNames: ['You', 'AI'],
    });
  };

  const handleLocalStart = () => {
    const names = playerNames.slice(0, playerCount);
    onStartGame({
      mode: 'local-multiplayer',
      playerCount,
      playerNames: names,
    });
  };

  const handleCreateRoom = () => {
    onStartGame({
      mode: 'multiplayer-realtime',
      playerCount: 2,
      isPrivate: true,
    });
  };

  const handleRoomCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6);
    setRoomCode(value);
    setRoomCodeError('');
  };

  const handleJoinRoom = async (e: React.FormEvent) => {
    e.preventDefault();
    if (roomCode.length !== 6) {
      setRoomCodeError('Room code must be 6 characters');
      return;
    }
    setIsJoining(true);
    try {
      await onJoinPrivateGame(roomCode);
    } catch (error: any) {
      setRoomCodeError(error.message || 'Failed to join room');
    } finally {
      setIsJoining(false);
    }
  };

  const handlePlayerNameChange = (index: number, name: string) => {
    const newNames = [...playerNames];
    newNames[index] = name;
    setPlayerNames(newNames);
  };

  // Mode selection screen
  if (step === 'mode') {
    return (
      <div className="golf-mode-selector">
        <div className="golf-mode-selector__header">
          <h2 className="golf-mode-selector__title">🏌️ Golf Card Game</h2>
          <p className="golf-mode-selector__subtitle">Choose your game mode</p>
        </div>

        {guestGamesRemaining !== undefined && !isAuthenticated && (
          <div className="golf-mode-selector__guest-banner">
            <span className="golf-mode-selector__guest-icon">👤</span>
            <span>{guestGamesRemaining} free games remaining</span>
          </div>
        )}

        <div className="golf-mode-selector__options">
          <button
            className="golf-mode-selector__card golf-mode-selector__card--single"
            onClick={() => handleModeSelect('singleplayer')}
          >
            <div className="golf-mode-selector__card-icon">🤖</div>
            <h3 className="golf-mode-selector__card-title">Single Player</h3>
            <p className="golf-mode-selector__card-desc">Play against AI with adjustable difficulty</p>
          </button>

          <button
            className="golf-mode-selector__card golf-mode-selector__card--local"
            onClick={() => handleModeSelect('local-multiplayer')}
          >
            <div className="golf-mode-selector__card-icon">👥</div>
            <h3 className="golf-mode-selector__card-title">Local Multiplayer</h3>
            <p className="golf-mode-selector__card-desc">2-4 players on the same device</p>
          </button>

          <button
            className="golf-mode-selector__card golf-mode-selector__card--online"
            onClick={() => handleModeSelect('multiplayer-realtime')}
          >
            <div className="golf-mode-selector__card-icon">🌐</div>
            <h3 className="golf-mode-selector__card-title">Online Multiplayer</h3>
            <p className="golf-mode-selector__card-desc">Play with friends online in real-time</p>
          </button>
        </div>
      </div>
    );
  }

  // Difficulty selection
  if (step === 'difficulty') {
    return (
      <div className="golf-mode-selector">
        <div className="golf-mode-selector__header">
          <button className="golf-mode-selector__back" onClick={handleBack} aria-label="Back to mode selection">
            ← Back
          </button>
          <h2 className="golf-mode-selector__title">AI Difficulty</h2>
          <p className="golf-mode-selector__subtitle">Choose your opponent's strength</p>
        </div>

        <div className="golf-mode-selector__options">
          <button
            className="golf-mode-selector__card golf-mode-selector__card--easy"
            onClick={() => handleDifficultySelect('easy')}
          >
            <div className="golf-mode-selector__card-icon">🌱</div>
            <h3 className="golf-mode-selector__card-title">Easy</h3>
            <p className="golf-mode-selector__card-desc">Random moves with slight preferences</p>
            <ul className="golf-mode-selector__card-features">
              <li>40% suboptimal moves</li>
              <li>No card counting</li>
              <li>Good for learning</li>
            </ul>
          </button>

          <button
            className="golf-mode-selector__card golf-mode-selector__card--medium"
            onClick={() => handleDifficultySelect('medium')}
          >
            <div className="golf-mode-selector__card-icon">🎯</div>
            <h3 className="golf-mode-selector__card-title">Medium</h3>
            <p className="golf-mode-selector__card-desc">Basic strategy with card tracking</p>
            <ul className="golf-mode-selector__card-features">
              <li>Seeks column pairs</li>
              <li>Replaces high cards</li>
              <li>Balanced challenge</li>
            </ul>
          </button>

          <button
            className="golf-mode-selector__card golf-mode-selector__card--hard"
            onClick={() => handleDifficultySelect('hard')}
          >
            <div className="golf-mode-selector__card-icon">👑</div>
            <h3 className="golf-mode-selector__card-title">Hard</h3>
            <p className="golf-mode-selector__card-desc">Card counting with optimal play</p>
            <ul className="golf-mode-selector__card-features">
              <li>Tracks all visible cards</li>
              <li>Probability-based decisions</li>
              <li>Optimal knock timing</li>
            </ul>
          </button>
        </div>
      </div>
    );
  }

  // Local multiplayer setup
  if (step === 'local-setup') {
    return (
      <div className="golf-mode-selector">
        <div className="golf-mode-selector__header">
          <button className="golf-mode-selector__back" onClick={handleBack} aria-label="Back to mode selection">
            ← Back
          </button>
          <h2 className="golf-mode-selector__title">Local Multiplayer</h2>
          <p className="golf-mode-selector__subtitle">Set up your pass-and-play game</p>
        </div>

        <div className="golf-mode-selector__setup">
          <div className="golf-mode-selector__field">
            <label className="golf-mode-selector__label" htmlFor="player-count">
              Number of Players
            </label>
            <div className="golf-mode-selector__player-count">
              {[2, 3, 4].map(count => (
                <button
                  key={count}
                  className={`golf-mode-selector__count-btn ${playerCount === count ? 'golf-mode-selector__count-btn--active' : ''}`}
                  onClick={() => setPlayerCount(count)}
                  aria-pressed={playerCount === count}
                >
                  {count}
                </button>
              ))}
            </div>
          </div>

          <div className="golf-mode-selector__field">
            <label className="golf-mode-selector__label">Player Names</label>
            <div className="golf-mode-selector__names">
              {Array.from({ length: playerCount }, (_, i) => (
                <input
                  key={i}
                  type="text"
                  className="golf-mode-selector__name-input"
                  value={playerNames[i]}
                  onChange={(e) => handlePlayerNameChange(i, e.target.value)}
                  placeholder={`Player ${i + 1}`}
                  maxLength={20}
                  aria-label={`Player ${i + 1} name`}
                />
              ))}
            </div>
          </div>

          <button
            className="golf-mode-selector__start-btn"
            onClick={handleLocalStart}
          >
            Start Game
          </button>
        </div>
      </div>
    );
  }

  // Online multiplayer setup
  if (step === 'online-setup') {
    return (
      <div className="golf-mode-selector">
        <div className="golf-mode-selector__header">
          <button className="golf-mode-selector__back" onClick={handleBack} aria-label="Back to mode selection">
            ← Back
          </button>
          <h2 className="golf-mode-selector__title">Online Multiplayer</h2>
          <p className="golf-mode-selector__subtitle">Play with friends online</p>
        </div>

        <div className="golf-mode-selector__setup">
          <button
            className="golf-mode-selector__create-btn"
            onClick={handleCreateRoom}
          >
            <span className="golf-mode-selector__create-icon">🏠</span>
            Create Room
          </button>

          <div className="golf-mode-selector__divider">
            <span>or</span>
          </div>

          <form className="golf-mode-selector__join-form" onSubmit={handleJoinRoom}>
            <label className="golf-mode-selector__label" htmlFor="room-code">
              Join a Room
            </label>
            <div className="golf-mode-selector__join-row">
              <input
                id="room-code"
                type="text"
                className={`golf-mode-selector__room-input ${roomCodeError ? 'golf-mode-selector__room-input--error' : ''}`}
                value={roomCode}
                onChange={handleRoomCodeChange}
                placeholder="Enter 6-character code"
                maxLength={6}
                disabled={isJoining}
                aria-describedby={roomCodeError ? 'room-code-error' : undefined}
              />
              <button
                type="submit"
                className="golf-mode-selector__join-btn"
                disabled={isJoining || roomCode.length !== 6}
              >
                {isJoining ? 'Joining...' : 'Join'}
              </button>
            </div>
            {roomCodeError && (
              <p id="room-code-error" className="golf-mode-selector__error" role="alert">
                {roomCodeError}
              </p>
            )}
          </form>
        </div>
      </div>
    );
  }

  return null;
};

export default GolfGameModeSelector;
